import React, {FC, useEffect, useState} from 'react'
import {Modal, Button, Spinner} from 'react-bootstrap'
import {rdStationApiService} from '../services/rdStationApi'

interface RdStationWebhookSetupModalProps {
  show: boolean
  onClose: () => void
}

const RdStationWebhookSetupModal: FC<RdStationWebhookSetupModalProps> = ({show, onClose}) => {
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return

    setLoading(true)
    setError(null)
    setCopied(false)

    rdStationApiService
      .getStatus()
      .then((status) => setWebhookUrl(status.webhook_url))
      .catch(() => setError('Não foi possível carregar a URL do webhook. Tente novamente.'))
      .finally(() => setLoading(false))
  }, [show])

  const copyWebhookUrl = async () => {
    if (!webhookUrl) return
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copie a URL do webhook:', webhookUrl)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Configurar webhook — RD Station CRM</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted mb-5'>
          A integração é feita no RD Station CRM. Siga os passos abaixo e use a URL gerada para sua
          instância.
        </p>

        <ol className='text-gray-700 mb-6 ps-4'>
          <li className='mb-2'>Acesse o RD Station CRM e abra as configurações de Webhooks.</li>
          <li className='mb-2'>Crie um novo webhook e cole a URL exibida abaixo.</li>
          <li className='mb-2'>
            Selecione o evento <strong>crm_deal_updated</strong>.
          </li>
          <li className='mb-2'>
            Quando uma negociação for atualizada para o status <strong>ganha (won)</strong>, um
            projeto será criado automaticamente no TaskRush.
          </li>
        </ol>

        <label className='form-label fw-semibold fs-6 mb-2'>URL do webhook</label>

        {loading ? (
          <div className='d-flex align-items-center gap-2 text-muted py-3'>
            <Spinner animation='border' size='sm' />
            Carregando URL do webhook...
          </div>
        ) : error ? (
          <div className='alert alert-danger py-3 mb-0'>{error}</div>
        ) : !webhookUrl ? (
          <div className='alert alert-warning py-3 mb-0'>
            Só administradores ou gestores da conta podem ver a URL completa do webhook, pois ela
            inclui um segredo de segurança da sua instância. Peça a um administrador ou gestor para
            configurar essa integração.
          </div>
        ) : (
          <>
            <div className='input-group mb-2'>
              <input
                type='text'
                className='form-control font-monospace fs-6'
                value={webhookUrl}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <Button variant='light-primary' onClick={copyWebhookUrl} title='Copiar URL'>
                <i className={`fas ${copied ? 'fa-check' : 'fa-copy'} me-1`}></i>
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            <p className='text-muted mb-0'>
              Essa URL é única para a sua conta e inclui um segredo de segurança — não compartilhe
              com terceiros nem altere nenhum dos parâmetros.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default RdStationWebhookSetupModal
